import React, { useState, useEffect } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { Table } from "@mui/material";
import Banner from "../Common/Banner";
import { RotatingTriangles } from "react-loader-spinner";
import SnackbarComponent from "../Common/SnackbarComponent";
import api from "../Auth/Middleware";
import { useNavigate } from "react-router-dom";

const AddScore = () => {
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [testDate, setTestDate] = useState("");
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [scores, setScores] = useState([]);
  const [snackbarMsg, setSnackbarMSg] = useState("");
  const [snackbarSevierty, setSnackbarSevierty] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [maxMarks, setMaxMarks] = useState(100);
  const navigate = useNavigate();

  const classes = [
    { name: "6th", subjects: ["Maths", "Science"] },
    { name: "7th", subjects: ["Maths", "Science"] },
    { name: "8th", subjects: ["Maths", "Science"] },
    { name: "9th", subjects: ["Maths", "Science"] },
    { name: "10th", subjects: ["Maths", "Science"] },
    { name: "11th", subjects: ["Maths", "Physics", "Chemistry"] },
    { name: "12th", subjects: ["Maths", "Physics", "Chemistry"] },
  ];

  const SubjectsEnum = {
    Maths: 1,
    Science: 2,
    Physics: 3,
    Chemistry: 4,
  };

  const handleClassChange = (event) => {
    const { value } = event.target;
    setSelectedClass(value);
    setSelectedSubject("");
  };

  const handleSubjectChange = (event) => {
    setSelectedSubject(event.target.value);
  };

  const handleTestDateChange = (event) => {
    setTestDate(event.target.value);
  };

  const handleScoreChange = (studentId, mark) => {
    const student = students.find((s) => s.id === studentId);

    if (student) {
      if (mark >= 0 && mark <= maxMarks) {
        const updatedScores = scores.map((s) =>
          s.studentId === studentId ? { ...s, mark } : s
        );
        setScores(updatedScores);
      } else {
        setSnackbarOpen(true);
        setSnackbarMSg(
          "Invalid mark entered. Marks should be between 0 and " + maxMarks
        );
        setSnackbarSevierty("warning");
      }
    }
  };

  const handleMaxMarksChange = (event) => {
    setMaxMarks(event.target.value);
  };

  useEffect(() => {
    // Fetch students data from API
    if (selectedClass && selectedSubject && testDate) {
      setIsLoading(true);
      const fetchData = async () => {
        try {
          const response = await api.get(
            `Student/StudentDetails?class=${selectedClass.slice(
              0,
              -2
            )}&course=${SubjectsEnum[selectedSubject]}&date=${testDate}`
          );
          setStudents(response.data.students);
          setMaxMarks(
            response.data.students[0]?.maxMarks !== 0
              ? response.data.students[0]?.maxMarks
              : 100
          );
          // Initialize scores with default values
          const defaultScores = response.data.students.map((student) => ({
            id: student.marksId !== 0 ? student.marksId : null,
            studentId: student.id,
            mark: student.marks,
          }));
          setScores(defaultScores);
          setIsLoading(false);
        } catch (error) {
          setIsLoading(false);
          setSnackbarOpen(true);
          setSnackbarMSg("Something went Wrong!! Please refresh the page");
          setSnackbarSevierty("error");
        }
      };
      fetchData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSubject, testDate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const patchData = {
      class: +selectedClass.slice(0, -2),
      course: SubjectsEnum[selectedSubject],
      dateOfExam: testDate,
      marks: scores,
      maxMarks: maxMarks,
    };
    try {
      const response = await api.patch("Student/UpdateMarks", patchData);
      if (response.status === 200) {
        handleReset();
        setSnackbarOpen(true);
        setSnackbarMSg(`Marks Updated Successfully for Class ${selectedClass}`);
        setSnackbarSevierty("success");
        navigate('/teacherDashboard');
        
      } else {
        setSnackbarOpen(true);
        setSnackbarMSg(`Something Went Wrong! Please try saving again`);
        setSnackbarSevierty("success");
      }
    } catch (error) {
      setSnackbarOpen(true);
      setSnackbarMSg("API Error Occured");
      setSnackbarSevierty("error");
    }
  };

  const closeSnackbar = () => {
    setSnackbarOpen(false);
  };

  const handleReset = () => {
    setSelectedClass("");
    setSelectedSubject("");
    setTestDate("");
    setStudents([]);
    setScores([]);
  };

  return (
    <>
      <SnackbarComponent
        open={snackbarOpen}
        severity={snackbarSevierty}
        message={snackbarMsg}
        onClose={closeSnackbar}
      ></SnackbarComponent>
      <Banner
        title="Add Score"
        currentpage="Add Score"
        previousPage="Teacher Dashboard"
        link="/teacherDashboard"
      />
      <div className="container my-5">
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-4 col-sm-12">
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel htmlFor="class">Class</InputLabel>
                    <Select
                      label="Class"
                      value={selectedClass}
                      onChange={handleClassChange}
                    >
                      {classes.map((cls) => (
                        <MenuItem key={cls.name} value={cls.name}>
                          {cls.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel htmlFor="subject">Subject</InputLabel>
                    <Select
                      label="Subject"
                      value={selectedSubject}
                      onChange={handleSubjectChange}
                      disabled={!selectedClass}
                    >
                      {selectedClass &&
                        classes
                          .find((cls) => cls.name === selectedClass)
                          .subjects.map((subject) => (
                            <MenuItem key={subject} value={subject}>
                              {subject}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Test"
                    value={testDate}
                    onChange={handleTestDateChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={!selectedClass || !selectedSubject}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Maximum Marks"
                    value={maxMarks}
                    onChange={handleMaxMarksChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    disabled={!selectedClass || !selectedSubject || !testDate}
                  />
                </Grid>
              </Grid>
            </div>
            <div className="col-md-2"></div>
            <div className="col-md-6 col-sm-12">
              <div className="text-center">
                {isLoading && <RotatingTriangles className="ml-2" />}
              </div>
              {students.length > 0 && (
                <div>
                  <Table>
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((student) => (
                        <tr key={student.id}>
                          <td>{student.firstName + " " + student.lastName}</td>
                          <td>
                            <input
                              className="form-control"
                              type="number"
                              value={
                                scores.find((s) => s.studentId === student.id)
                                  ?.mark || ""
                              }
                              onChange={(e) =>
                                handleScoreChange(student.id, +e.target.value)
                              }
                              disabled={isLoading}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <Button
                    className="my-5"
                    variant="contained"
                    type="submit"
                    disabled={isLoading || !students.length}
                  >
                    Submit
                  </Button>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddScore;
